import moment from 'moment'

import { HIDE_SALES_FORECAST_ADJUSTMENT } from '../constants'
import {
  IPrepItemBase,
  calculatePrepItemValues,
  formatCurrency,
  formatNonPrepItemValue,
  formatPrepItemValue,
  getOnHandUnitLabel,
  isBucketItem,
} from './prepItemCalculations'

interface IPrintBanner {
  type: 'delivery' | 'day-before'
  deliveryDays: string[]
  activeDayLabel: string
}

interface IPrintData {
  locationName: string
  date: moment.Moment | null
  baseForecast: number
  adjustment: number
  weeklySalesForecast: number
  prepItems: IPrepItemBase[]
  nonPrepItems?: IPrepItemBase[]
  onHandValues: Record<string, Record<string, number>>
  banner?: IPrintBanner | null
}

const renderBanner = (banner: IPrintBanner | null | undefined): string => {
  if (!banner) return ''
  // why: mirror the on-screen banner copy verbatim (Nikki's approved wording).
  // Colors use the same blue/amber palette as the live banners.
  if (banner.type === 'delivery') {
    const chips = (banner.deliveryDays || [])
      .map((d) => {
        const active = d.toLowerCase() === banner.activeDayLabel
        const bg = active ? '#1976d2' : '#e0e0e0'
        const color = active ? 'white' : '#333'
        return `<span style="display:inline-block;padding:2px 8px;border-radius:12px;background:${bg};color:${color};font-size:11px;font-weight:600;margin-right:4px;">${d}</span>`
      })
      .join('')
    return `
      <div class="banner banner-delivery">
        <strong>🚚 Delivery Day</strong> &mdash; Today is a delivery day. Only items listed under Prep Guide are scheduled to be prepped today. Prep amounts for Non-Prep items have been redistributed to previous days to minimize prep and allow focus on putting away the delivery. On-hand counts must still be entered for all items. If you are at risk of running out of any product, prep as needed.
        <div style="margin-top:4px;">${chips}</div>
      </div>
    `
  }
  return `
    <div class="banner banner-day-before">
      <strong>📦 Day Before Delivery</strong> &mdash; Tomorrow is a delivery day. Prep amounts for items that will not be prepped tomorrow have been redistributed to today, so prep quantities may be slightly higher than usual.
    </div>
  `
}

export const printPrepGuide = (data: IPrintData) => {
  const {
    locationName,
    date,
    baseForecast,
    adjustment,
    weeklySalesForecast,
    prepItems,
    nonPrepItems = [],
    onHandValues,
    banner,
  } = data

  const adjustedForecast = baseForecast * (1 + adjustment / 100)

  const getOnHandValue = (itemName: string, batchType: string) => {
    return onHandValues[itemName]?.[batchType] || 0
  }

  // Note: Each item will show its own batch types in the table

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Prep Guide - ${locationName}</title>
      <style>
        @page {
          size: letter landscape;
          margin: 0.5in;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          margin: 0;
          padding: 0;
          color: #333;
          font-size: 11px;
        }

        .header {
          text-align: center;
          margin-bottom: 12px;
          border-bottom: 1px solid #333;
          padding-bottom: 8px;
        }

        .header h1 {
          margin: 0 0 4px 0;
          font-size: 18px;
          font-weight: 600;
        }

        .header p {
          margin: 0;
          font-size: 12px;
          color: #666;
        }

        .forecast-section {
          margin-bottom: 12px;
          display: flex;
          justify-content: space-around;
          padding: 6px;
          border: 1px solid #333;
        }

        .forecast-item {
          text-align: center;
        }

        .forecast-label {
          font-size: 9px;
          color: #666;
          display: block;
        }

        .forecast-value {
          font-size: 11px;
          font-weight: 600;
        }

        .prep-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 9px;
          margin-top: 8px;
        }

        .prep-table th {
          padding: 3px 1px;
          text-align: center;
          font-weight: 600;
          font-size: 8px;
          border: 1px solid #333;
        }

        .prep-table td {
          padding: 2px 1px;
          text-align: center;
          border: 1px solid #ddd;
          font-size: 8px;
        }

        .prep-table tr:nth-child(even) {
          /* Removed background color for black and white printing */
        }

        .item-name-cell {
          text-align: left !important;
          font-weight: 600;
          padding-left: 4px !important;
          max-width: 100px;
          font-size: 9px;
        }

        .prep-value-cell {
          font-weight: 700;
          font-size: 9px;
          border: 2px solid #333 !important;
        }

        .footer {
          margin-top: 8px;
          text-align: center;
          font-size: 8px;
          color: #666;
        }

        .banner {
          margin: 8px 0 12px 0;
          padding: 8px 10px;
          border-radius: 4px;
          font-size: 10px;
          line-height: 1.35;
        }

        .banner-delivery {
          background: #e3f2fd;
          border: 1px solid #1976d2;
          color: #0d47a1;
        }

        .banner-day-before {
          background: #fff8e1;
          border: 1px solid #f9a825;
          color: #6d4c00;
        }

        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${locationName}</h1>
        <p>${date?.format('dddd, MMMM D, YYYY') || ''}</p>
      </div>

      ${renderBanner(banner)}

      <div class="forecast-section">
        <div class="forecast-item">
          <span class="forecast-label">Weekly Forecast</span>
          <span class="forecast-value">${formatCurrency(
            weeklySalesForecast,
          )}</span>
        </div>
        <div class="forecast-item">
          <span class="forecast-label">Base Forecast</span>
          <span class="forecast-value">${formatCurrency(baseForecast)}</span>
        </div>
${
  HIDE_SALES_FORECAST_ADJUSTMENT
    ? ''
    : `<div class="forecast-item">
          <span class="forecast-label">Adjusted Forecast</span>
          <span class="forecast-value">${formatCurrency(
            adjustedForecast,
          )}</span>
        </div>
        <div class="forecast-item">
          <span class="forecast-label">Adjustment</span>
          <span class="forecast-value">${adjustment}%</span>
        </div>
`
}
      </div>
      <table class="prep-table">
        <thead>
          <tr>
            <th style="width: 120px;">Item</th>
            <th style="width: 40px;">Batch 1</th>
            <th style="width: 40px;">Batch 2</th>
            <th style="width: 40px;">Batch 3</th>
            <th style="width: 50px;">On Hand</th>
            <th style="width: 50px;">Needed</th>
            <th style="width: 50px;">Prep</th>
          </tr>
        </thead>
        <tbody>
          ${prepItems
            .filter((item) => {
              const calculations = calculatePrepItemValues(
                item,
                adjustment,
                onHandValues,
              )
              // Only show items that need prep (insufficient on hand)
              return calculations.prep > 0
            })
            .map((item) => {
              const calculations = calculatePrepItemValues(
                item,
                adjustment,
                onHandValues,
              )
              const onHandUnitLabel = getOnHandUnitLabel(item)
              const details = item.prepOnHandDetails || []
              const isCCItem = isBucketItem(item.prepItemIdCode)
              const neededLabel = isCCItem ? 'Bucket' : item.prepBatchType
              const prepLabel = isCCItem ? 'Bucket' : item.prepBatchType

              return `
                <tr>
                  <td class="item-name-cell">${item.prepItemName}</td>
                  ${Array.from({ length: 3 })
                    .map((_, i) => {
                      const detail = details[i]
                      if (detail) {
                        const value = getOnHandValue(
                          item.prepItemName,
                          detail.prepBatchType,
                        )
                        return `<td><small>${detail.prepBatchType}</small><br>${
                          typeof value === 'number'
                            ? Number.isInteger(value)
                              ? value.toString()
                              : value.toFixed(1)
                            : value
                        }</td>`
                      }
                      return '<td>-</td>'
                    })
                    .join('')}
                  <td>${calculations.displayOnHand.toFixed(
                    1,
                  )}<br><small>(${onHandUnitLabel})</small></td>
                  <td>${calculations.needed.toFixed(
                    1,
                  )}<br><small>(${neededLabel})</small></td>
                  <td class="prep-value-cell">${formatPrepItemValue(
                    calculations.prep,
                  )}<br><small>(${prepLabel})</small></td>
                </tr>
              `
            })
            .join('')}
        </tbody>
      </table>

      ${
        nonPrepItems.length > 0
          ? `
      <h2 style="margin-top: 20px; margin-bottom: 8px; font-size: 14px; font-weight: 600;">Non-Prep Items (Not Scheduled Today)</h2>
      <table class="prep-table">
        <thead>
          <tr>
            <th style="width: 120px;">Item</th>
            <th style="width: 40px;">Batch 1</th>
            <th style="width: 40px;">Batch 2</th>
            <th style="width: 40px;">Batch 3</th>
            <th style="width: 50px;">On Hand</th>
            <th style="width: 50px;">Needed</th>
            <th style="width: 50px;">Prep</th>
          </tr>
        </thead>
        <tbody>
          ${nonPrepItems
            .filter((item) => {
              const calculations = calculatePrepItemValues(
                item,
                adjustment,
                onHandValues,
              )
              // Only show items that need prep (insufficient on hand)
              return calculations.prep > 0
            })
            .map((item) => {
              const calculations = calculatePrepItemValues(
                item,
                adjustment,
                onHandValues,
              )
              const onHandUnitLabel = getOnHandUnitLabel(item)
              const details = item.prepOnHandDetails || []
              const isCCItem = isBucketItem(item.prepItemIdCode)
              const neededLabel = isCCItem ? 'Bucket' : item.prepBatchType
              const prepLabel = isCCItem ? 'Bucket' : item.prepBatchType

              return `
                <tr>
                  <td class="item-name-cell">${item.prepItemName}</td>
                  ${Array.from({ length: 3 })
                    .map((_, i) => {
                      const detail = details[i]
                      if (detail) {
                        const value = getOnHandValue(
                          item.prepItemName,
                          detail.prepBatchType,
                        )
                        return `<td><small>${detail.prepBatchType}</small><br>${
                          typeof value === 'number'
                            ? Number.isInteger(value)
                              ? value.toString()
                              : value.toFixed(1)
                            : value
                        }</td>`
                      }
                      return '<td>-</td>'
                    })
                    .join('')}
                  <td>${calculations.displayOnHand.toFixed(
                    1,
                  )}<br><small>(${onHandUnitLabel})</small></td>
                  <td>${calculations.needed.toFixed(
                    1,
                  )}<br><small>(${neededLabel})</small></td>
                  <td class="prep-value-cell">${formatNonPrepItemValue(
                    calculations.prep,
                  )}<br><small>(${prepLabel})</small></td>
                </tr>
              `
            })
            .join('')}
        </tbody>
      </table>
      `
          : ''
      }

      <div class="footer">
        Generated on ${moment().format(
          'M/D/YYYY, h:mm:ss A',
        )} | ${locationName} Prep Guide
      </div>
    </body>
    </html>
  `

  const printWindow = window.open('', '_blank')

  if (printWindow) {
    printWindow.document.write(printContent)
    printWindow.document.close()

    // Wait for content to load then trigger print
    printWindow.onload = () => {
      printWindow.print()
    }
  }
}
